import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

import Button from 'ui/Button';

// Per-SKU allocations (SELL06): a variant product is allocated per combination
// — blank means that SKU is not sold at this event (sparse). Variant-less
// products keep the single event quantity.
const buildVariantQuantities = (product, listing) => {
  const variants = (listing?.variants || product?.variants || []);
  const allocated = listing?.variant_allocations || [];
  return variants.map((variant) => {
    const existing = allocated.find((row) => row.product_variant_id === variant.id);
    return {
      product_variant_id: variant.id,
      label: variant.label,
      available_amount: variant.available_amount,
      quantity: existing ? String(existing.allocation_quantity) : '',
    };
  });
};

const SelectStoreProductModal = ({ products, listing, onClose, onSave }) => {
  const [productId, setProductId] = useState(listing?.product_id || '');
  const [quantity, setQuantity] = useState(listing?.allocation_quantity || 1);
  const [variantQuantities, setVariantQuantities] = useState(buildVariantQuantities(null, listing));

  const selectedProduct = products.find((product) => product.id === productId);
  const hasVariants = variantQuantities.length > 0;

  useEffect(() => {
    setProductId(listing?.product_id || '');
    setQuantity(listing?.allocation_quantity || 1);
    setVariantQuantities(buildVariantQuantities(null, listing));
  }, [listing]);

  useEffect(() => {
    if (!listing) setVariantQuantities(buildVariantQuantities(selectedProduct, null));
  }, [productId]);

  const handleVariantQuantityChange = (variantId, value) => {
    setVariantQuantities((prev) =>
      prev.map((row) => (row.product_variant_id === variantId ? { ...row, quantity: value } : row))
    );
  };

  const validRow = (value) => value === '' || (Number.isInteger(Number(value)) && Number(value) > 0);
  const filledRows = variantQuantities.filter((row) => row.quantity !== '');
  const validQuantity = Number.isInteger(Number(quantity)) && Number(quantity) > 0;
  const canSave = !!productId && (hasVariants
    ? filledRows.length > 0 && variantQuantities.every((row) => validRow(row.quantity))
    : validQuantity);

  const handleSave = () => {
    if (hasVariants) {
      onSave({
        productId: Number(productId),
        variantAllocations: filledRows.map((row) => ({
          product_variant_id: row.product_variant_id,
          allocation_quantity: Number(row.quantity),
        })),
      });
    } else {
      onSave({ productId: Number(productId), quantity: Number(quantity) });
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{listing ? 'Edit event quantity' : 'Select from Store'}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          We&apos;ll set this quantity aside 2 hours before the event. Anything left becomes available online afterward.
        </DialogContentText>

        {!listing && products.length === 0 &&
          <DialogContentText sx={{ mb: 2 }}>There are no other Store products available to add.</DialogContentText>}

        <FormControl fullWidth sx={{ mb: 2 }} color="warning" disabled={!!listing}>
          <InputLabel id="event-store-product-label">Product</InputLabel>
          <Select
            labelId="event-store-product-label"
            label="Product"
            value={productId}
            onChange={(event) => setProductId(event.target.value)}
          >
            {listing && (
              <MenuItem value={listing.product_id}>{listing.identifier}</MenuItem>
            )}
            {products.map((product) => (
              <MenuItem value={product.id} key={product.id}>
                {product.identifier}
                {product.available_amount != null ? ` (${product.available_amount} available online)` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasVariants ? (
          <>
            <DialogContentText sx={{ mb: 1 }}>
              Quantity per option at this event — leave blank to not sell that option here.
            </DialogContentText>
            {variantQuantities.map((row) => (
              <TextField
                key={row.product_variant_id}
                fullWidth
                sx={{ mb: 1 }}
                type="number"
                color="warning"
                label={row.available_amount != null
                  ? `${row.label} (${row.available_amount} available online)`
                  : row.label}
                inputProps={{ min: 1, step: 1 }}
                value={row.quantity}
                error={!validRow(row.quantity)}
                helperText={!validRow(row.quantity) ? 'Enter a positive whole number or leave blank.' : ''}
                onChange={(event) => handleVariantQuantityChange(row.product_variant_id, event.target.value)}
              />
            ))}
          </>
        ) : (
          <TextField
            fullWidth
            type="number"
            color="warning"
            label="Quantity at this event"
            inputProps={{ min: 1, step: 1 }}
            value={quantity}
            error={!validQuantity}
            helperText={!validQuantity ? 'Enter a positive whole number.' : ''}
            onChange={(event) => setQuantity(event.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button secondary onClick={onClose}>Cancel</Button>
        <Button disabled={!canSave} onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SelectStoreProductModal.propTypes = {
  products: PropTypes.array.isRequired,
  listing: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default SelectStoreProductModal;
